import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function EmailMarketing() {
  const [emailAddress, setEmailAddress] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const generateEmailTemplate = async () => {
    try {
      // Try multiple paths to find the email template
      const paths = [
        '/email-template.html',
        './email-template.html',
        '../email-template.html',
        '../../email-template.html'
      ];
      
      let htmlContent = '';
      let foundPath = '';
      
      for (const path of paths) {
        try {
          const response = await fetch(path);
          if (response.ok) {
            const text = await response.text();
            // Verify it's the email template by checking for specific content
            if (text.includes('QBOVerifier') && text.includes('Invoice Fraud') && text.includes('table role="presentation"')) {
              htmlContent = text;
              foundPath = path;
              break;
            }
          }
        } catch (e) {
          continue;
        }
      }
      
      if (!htmlContent) {
        throw new Error('Email template not found in any expected location');
      }
      
      // Log to verify content is loaded
      console.log('✅ Template loaded from:', foundPath);
      console.log('📄 Template length:', htmlContent.length);
      console.log('📄 First 500 chars:', htmlContent.substring(0, 500));
      
      // CRITICAL: Check for emojis vs Remix Icons
      console.log('🔍 Contains emoji ⚠️:', htmlContent.includes('⚠️'));
      console.log('🔍 Contains emoji 🛡️:', htmlContent.includes('🛡️'));
      console.log('🔍 Contains emoji 👤:', htmlContent.includes('👤'));
      console.log('🔍 Contains Remix Icon class ri-error-warning-fill:', htmlContent.includes('ri-error-warning-fill'));
      console.log('🔍 Contains Remix Icon class ri-shield-cross-fill:', htmlContent.includes('ri-shield-cross-fill'));
      
      // Find and log the "Lost Revenue" section specifically
      const lostRevenueIndex = htmlContent.indexOf('Lost Revenue');
      if (lostRevenueIndex > -1) {
        const snippet = htmlContent.substring(Math.max(0, lostRevenueIndex - 200), lostRevenueIndex + 200);
        console.log('📍 Lost Revenue section:', snippet);
      }
      
      return htmlContent;
    } catch (error) {
      console.error('❌ Error loading email template:', error);
      throw error;
    }
  };

  const handleDownload = async () => {
    try {
      // Get the email template HTML
      const templateHTML = await generateEmailTemplate();
      
      // Create a blob and download
      const blob = new Blob([templateHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'qboverifier-email-template.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('✅ Template downloaded successfully');
    } catch (error) {
      console.error('❌ Download error:', error);
      setSendStatus({ type: 'error', message: 'Failed to download template' });
    }
  };

  const handleEmailTemplate = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      setSendStatus({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }

    setIsSending(true);
    setSendStatus(null);

    try {
      // Get the HTML template from the actual file
      const htmlTemplate = await generateEmailTemplate();
      
      // Verify we have content
      if (!htmlTemplate || htmlTemplate.length < 100) {
        throw new Error('Email template is empty or too short');
      }

      console.log('📧 Preparing to send email...');
      console.log('📬 To:', emailAddress);
      console.log('📦 Template size:', htmlTemplate.length, 'characters');

      // Convert HTML to Base64 with proper UTF-8 encoding
      const base64Body = btoa(unescape(encodeURIComponent(htmlTemplate)));
      
      console.log('🔐 Base64 size:', base64Body.length, 'characters');

      // Prepare the request payload
      const payload = {
        to: emailAddress,
        body: base64Body,
        code: 't8iPKqMORPX@'
      };

      console.log('🚀 Sending POST request to bio-mail.com endpoint...');
      console.log('📦 Payload keys:', Object.keys(payload));

      // Send email using your bio-mail.com endpoint with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      try {
        const response = await fetch('https://bio-mail.com/marketing/email-issue.asp', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        console.log('📡 Response received!');
        console.log('📡 Response status:', response.status);
        console.log('📡 Response statusText:', response.statusText);
        console.log('📡 Response ok:', response.ok);
        console.log('📡 Response headers:', Array.from(response.headers.entries()));
        
        // Get response text first
        const responseText = await response.text();
        console.log('📄 Raw response length:', responseText.length);
        console.log('📄 Raw response (first 1000 chars):', responseText.substring(0, 1000));
        
        if (!response.ok) {
          throw new Error(`Server returned ${response.status} ${response.statusText}: ${responseText.substring(0, 200)}`);
        }

        // Check content type
        const contentType = response.headers.get('content-type') || '';
        console.log('📄 Content-Type:', contentType);

        // Handle different response types
        if (contentType.includes('application/json')) {
          // JSON response
          try {
            const result = JSON.parse(responseText);
            console.log('📨 Parsed JSON response:', result);
            
            if (result.success) {
              setSendStatus({
                type: 'success',
                message: `✅ Template sent to ${emailAddress}! Check your inbox (and spam folder).`,
              });
              setEmailAddress('');
              console.log('✅ Email sent successfully!');
            } else {
              throw new Error(result.message || result.error || 'Server returned success=false');
            }
          } catch (parseError: any) {
            console.error('❌ JSON parse error:', parseError);
            throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
          }
        } else {
          // HTML or plain text response - assume success if status is 200
          console.log('📄 Non-JSON response received, assuming success based on 200 status');
          setSendStatus({
            type: 'success',
            message: `✅ Template sent to ${emailAddress}! Check your inbox (and spam folder).`,
          });
          setEmailAddress('');
          console.log('✅ Email sent successfully (non-JSON response)!');
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        console.error('❌ Fetch error details:', {
          name: fetchError.name,
          message: fetchError.message,
          stack: fetchError.stack,
          type: typeof fetchError,
          constructor: fetchError.constructor?.name,
          keys: Object.keys(fetchError)
        });
        
        if (fetchError.name === 'AbortError') {
          throw new Error('⏱️ Request timeout - the server took too long to respond (30 seconds). Please try again.');
        }
        
        if (fetchError.message.includes('Failed to fetch')) {
          throw new Error('🌐 Network error: Cannot connect to bio-mail.com. This could be a CORS issue, network problem, or the server may be down. Please check your internet connection and try again.');
        }
        
        throw fetchError;
      }
    } catch (error: any) {
      console.error('❌ Email send error:', error);
      console.error('❌ Error type:', typeof error);
      console.error('❌ Error constructor:', error?.constructor?.name);
      console.error('❌ Error keys:', error ? Object.keys(error) : 'null');
      console.error('❌ Error stringified:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      
      let errorMessage = 'Failed to send email. ';
      
      if (error instanceof Error) {
        errorMessage = `❌ ${error.message}`;
      } else if (error && typeof error === 'object') {
        // Handle non-Error objects
        if (error.message) {
          errorMessage = `❌ ${error.message}`;
        } else if (error.error) {
          errorMessage = `❌ ${error.error}`;
        } else {
          errorMessage = `❌ An unexpected error occurred: ${JSON.stringify(error)}`;
        }
      } else {
        errorMessage = '❌ An unexpected error occurred. Please check the browser console for details.';
      }
      
      setSendStatus({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-[950px] mx-auto px-6 py-12">
        {/* Download & Email Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-2 border-teal-500">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              <i className="ri-download-cloud-line text-teal-600 mr-2"></i>
              Get Your Email Template
            </h2>
            <p className="text-gray-600">
              Download the HTML file or have it emailed to you
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Download Button */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg p-6 border border-teal-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <i className="ri-download-2-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Download HTML</h3>
                  <p className="text-sm text-gray-600">Save to your computer</p>
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="w-full bg-teal-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-download-line"></i>
                Download Template
              </button>
            </div>

            {/* Email Form */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <i className="ri-mail-send-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Email Template</h3>
                  <p className="text-sm text-gray-600">Send to your inbox</p>
                </div>
              </div>
              <div className="space-y-3">
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  onClick={handleEmailTemplate}
                  disabled={isSending}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSending ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      Email Template
                    </>
                  )}
                </button>
              </div>
              
              {sendStatus && (
                <div className={`mt-4 p-3 rounded-lg text-sm ${
                  sendStatus.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  <div className="flex items-start gap-2">
                    <i className={`${sendStatus.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} mt-0.5`}></i>
                    <span>{sendStatus.message}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              <i className="ri-information-line text-gray-500 mr-1"></i>
              The template is ready to upload to Mailchimp, SendGrid, Constant Contact, or any email marketing platform
            </p>
          </div>
        </div>

        {/* Container with max width for email */}
        <div className="mx-auto" style={{ maxWidth: '950px' }}>
          
          {/* Hero Section with Background Image */}
          <section 
            className="relative min-h-[600px] flex items-center"
            style={{
              backgroundImage: `url('https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/023e88077fff9d769802d50d8ceba95c.jpeg')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
            <div className="relative z-10 px-12 py-20 w-full">
              <div className="max-w-2xl">
                <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                  Protect Your<br />
                  <span style={{ color: '#29B3E6' }}>Business Reputation</span><br />
                  From Scam Invoices
                </h1>
                <p className="text-xl text-white/90 mb-8 leading-relaxed">
                  Protect your business reputation and prevent fraud with QBOVerifier. <span style={{ color: '#29B3E6' }}>"Digitally Stamp"</span> every invoice with a unique cryptographic verification code that proves authenticity and builds client trust.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/pricing"
                    className="inline-block text-center bg-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                    style={{ color: '#29B3E6' }}
                  >
                    <i className="ri-rocket-line mr-2"></i>
                    Start Free Today
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="inline-block text-center border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-all whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line mr-2"></i>
                    See How It Works
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* The Growing Threat Section */}
          <section className="py-16 px-12" style={{ backgroundColor: '#F8F4EF' }}>
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Invoice Fraud is Costing Businesses Millions
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Business Email Compromise (BEC) and Business Invoice Fraud (BIF) are growing threats that damage reputations and cost money.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-error-warning-fill text-red-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Lost Revenue</h3>
                <p className="text-gray-600 leading-relaxed">
                  Clients pay fake invoices instead of legitimate ones, causing direct financial losses to your business.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-shield-cross-fill text-red-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Damaged Reputation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Fraudulent invoices using your business name destroy client trust and damage your professional reputation.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-user-unfollow-fill text-red-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Client Loss</h3>
                <p className="text-gray-600 leading-relaxed">
                  Confused and frustrated clients may stop doing business with you after falling victim to invoice fraud.
                </p>
              </div>
            </div>
          </section>

          {/* The Solution Section with Image */}
          <section className="py-16 px-12 bg-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  The QBOVerifier Solution
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Our Chrome extension seamlessly integrates with QuickBooks Online to add a unique verification stamp to every invoice you send. Your clients can instantly verify authenticity.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#E6F5FD' }}>
                      <i className="ri-check-line text-xl" style={{ color: '#29B3E6' }}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Automatic Verification Stamps</h4>
                      <p className="text-gray-600">Every invoice gets a unique QR code and verification number that proves it came from your QuickBooks account.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#E6F5FD' }}>
                      <i className="ri-check-line text-xl" style={{ color: '#29B3E6' }}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Instant Verification</h4>
                      <p className="text-gray-600">Clients verify invoice authenticity instantly with a simple click - no technical knowledge required</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#E6F5FD' }}>
                      <i className="ri-check-line text-xl" style={{ color: '#29B3E6' }}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Fraud Prevention</h4>
                      <p className="text-gray-600">Impossible to replicate verification codes make it virtually impossible for fraudsters to create convincing fakes.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#E6F5FD' }}>
                      <i className="ri-check-line text-xl" style={{ color: '#29B3E6' }}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Professionally Styled Invoices</h4>
                      <p className="text-gray-600">Use our Professional styled invoice layouts, where you can add your own header graphics. Or create your own styling using our Invoice Styler.</p>
                    </div>
                  </div>
                </div>

                <Link
                  to="/pricing"
                  className="inline-block text-center px-8 py-4 rounded-lg font-bold text-xl transition-all shadow-lg hover:shadow-xl whitespace-nowrap text-white"
                  style={{ backgroundColor: '#29B3E6' }}
                >
                  Get Started Free
                </Link>
              </div>

              <div className="relative">
                <div 
                  className="rounded-xl shadow-2xl overflow-hidden"
                  style={{ backgroundColor: '#F1F1EF' }}
                >
                  <img 
                    src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/8b792743fab37a62962de513d3840603.jpeg"
                    alt="QBOVerifier verified invoice"
                    className="w-full"
                    style={{ imageRendering: 'crisp-edges' }}
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg">
                  <i className="ri-shield-check-fill text-xl mr-2"></i>
                  <span className="font-semibold">Verified Authentic</span>
                </div>
              </div>
            </div>
          </section>

          {/* Key Features Section */}
          <section className="py-16 px-12" style={{ backgroundColor: '#E6F5FD' }}>
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Powerful Protection Features
              </h2>
              <p className="text-xl text-gray-700">
                Everything you need to secure your invoices and protect your business
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#E6F5FD' }}>
                  <i className="ri-qr-code-line text-3xl" style={{ color: '#29B3E6' }}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">QR Code Verification</h3>
                <p className="text-gray-600 leading-relaxed">
                  Each invoice includes a unique bioQR code that clients can scan to instantly verify authenticity through our bioMail platform.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Tamper-Proof Stamps</h3>
                <p className="text-gray-600 leading-relaxed">
                  Advanced cryptographic signatures ensure that verification stamps cannot be copied or replicated by fraudsters.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-time-line text-purple-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Real-Time Verification</h3>
                <p className="text-gray-600 leading-relaxed">
                  Instant verification results help clients make confident payment decisions without delays or uncertainty.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-bar-chart-line text-indigo-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Analytics Dashboard</h3>
                <p className="text-gray-600 leading-relaxed">
                  Track verification attempts, monitor security threats, and gain insights into your invoice security performance.
                </p>
              </div>
            </div>
          </section>

          {/* How It Works Section */}
          <section className="py-16 px-12 bg-white">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-xl text-gray-700">
                Get started in minutes with our simple 3-step process
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold" style={{ backgroundColor: '#29B3E6' }}>
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Install Extension</h3>
                <p className="text-gray-600">
                  Add our free Chrome extension to your browser in just one click.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold" style={{ backgroundColor: '#29B3E6' }}>
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Connect QuickBooks</h3>
                <p className="text-gray-600">
                  Link your QuickBooks Online account securely with our extension.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-3xl font-bold" style={{ backgroundColor: '#29B3E6' }}>
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Send Verified Invoices</h3>
                <p className="text-gray-600">
                  Every invoice you send automatically includes a verification stamp.
                </p>
              </div>
            </div>
          </section>

          {/* FREE Plan Highlight */}
          <section className="py-16 px-12 text-white relative overflow-hidden" style={{ backgroundColor: '#29B3E6' }}>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>
            
            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <div className="inline-block bg-white/20 px-6 py-2 rounded-full mb-6">
                <span className="text-lg font-semibold">LIMITED TIME OFFER</span>
              </div>
              <h2 className="text-5xl font-bold mb-6">
                Start FREE Today!
              </h2>
              <p className="text-2xl mb-4">
                50 Verified Invoices Per Month
              </p>
              <p className="text-xl mb-8 opacity-90">
                No credit card required • Full features included • Cancel anytime
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/pricing"
                  className="inline-block text-center bg-white px-10 py-5 rounded-lg font-bold text-xl transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                  style={{ color: '#29B3E6' }}
                >
                  <i className="ri-gift-line mr-2"></i>
                  Claim Your Free Account
                </Link>
              </div>

              <div className="flex items-center justify-center gap-8 text-white/80">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-xl"></i>
                  <span>No Credit Card</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-xl"></i>
                  <span>2-Min Setup</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-star-fill text-xl"></i>
                  <span>5-Star Rated</span>
                </div>
              </div>
            </div>
          </section>

          {/* For Businesses & Clients Section */}
          <section className="py-16 px-12" style={{ backgroundColor: '#F8F4EF' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-white rounded-xl p-10 shadow-lg">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#E6F5FD' }}>
                  <i className="ri-building-line text-3xl" style={{ color: '#29B3E6' }}></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  For Businesses Sending Invoices
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                    <span className="text-gray-700">Protect your business reputation from fraudsters</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                    <span className="text-gray-700">Build trust with clients through verified invoices</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                    <span className="text-gray-700">Prevent payment delays from suspicious clients</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                    <span className="text-gray-700">Stand out from competitors with verified credentials</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-10 shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-user-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  For Clients Receiving Invoices
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                    <span className="text-gray-700">Instantly verify invoice authenticity before paying</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                    <span className="text-gray-700">Protect your business from BEC and BIF fraud</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                    <span className="text-gray-700">Avoid paying fraudulent invoices to scammers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 text-xl mt-1"></i>
                    <span className="text-gray-700">Insist your vendors use QBOVerifier for safety</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Testimonial Section */}
          <section className="py-16 px-12 bg-white">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-500 text-3xl"></i>
                ))}
              </div>
              <blockquote className="text-2xl text-gray-700 italic mb-6 leading-relaxed">
                "QBOVerifier has completely eliminated our invoice fraud concerns. Our clients love the instant verification, and we've seen a significant increase in trust and faster payments. It's a must-have for any business sending invoices."
              </blockquote>
              <div className="flex items-center justify-center gap-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-gray-600 text-2xl"></i>
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900">Sarah Mitchell</p>
                  <p className="text-gray-600">CFO, TechStart Solutions</p>
                </div>
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="py-16 px-12 text-center text-white" style={{ backgroundColor: '#1E293B' }}>
            <h2 className="text-4xl font-bold mb-6">
              Ready to Protect Your Business?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
              Join thousands of businesses already using QBOVerifier to secure their invoices and protect their reputation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link
                to="/pricing"
                className="inline-block text-center bg-white px-10 py-5 rounded-lg font-bold text-xl transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                style={{ color: '#29B3E6' }}
              >
                <i className="ri-rocket-line mr-2"></i>
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                className="inline-block text-center border-2 border-white text-white px-10 py-5 rounded-lg font-bold text-xl hover:bg-white/10 transition-all whitespace-nowrap"
              >
                <i className="ri-customer-service-line mr-2"></i>
                Contact Sales
              </Link>
            </div>

            <div className="flex items-center justify-center gap-8 text-white/80">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-xl"></i>
                <span>99.999% Secure</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-download-line text-xl"></i>
                <span>2,200+ Downloads</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-star-fill text-xl"></i>
                <span>5-Star Rated</span>
              </div>
            </div>
          </section>

        </div>
      </div>

      <Footer />
    </div>
  );
}
